import { apiPatientPortalContract } from '@montugroup/pms-api-contracts';
import type { ClientInferResponses } from '@ts-rest/core';

import settings from '@/constants/constants';
import { QueryKey } from '@/constants/query';
import { useQuery } from '@tanstack/react-query';
import { initQueryClient } from '@ts-rest/react-query';
import { useMemo, useRef } from 'react';
import { z } from 'zod';

const ErrorSchema = z.object({
  error: z.object({
    message: z.string(),
  }),
});

export type ValidateConsultationDiscountResponse = ClientInferResponses<
  typeof apiPatientPortalContract.validateConsultationDiscount
>;

export type UseValidateDiscountCodeResult = {
  isValidated?: boolean;
  data?: ValidateConsultationDiscountResponse;
  isError: boolean;
  errorMessage?: string;
  isLoading: boolean;
  refetch: () => void;
};

/**
 * Helper function to get the error message from the response
 *
 * @param response - The response from the API
 * @returns - The error message from the response
 */
function errorMessageFromResponse(response: ValidateConsultationDiscountResponse): string {
  const DEFAULT_ERROR = 'Unable to validate this discount code. Please try again.';

  const result = ErrorSchema.safeParse(response.body);

  if (result.success) {
    return result.data.error.message;
  }

  return DEFAULT_ERROR;
}

/**
 * Validates a consultation discount code via `validateConsultationDiscount`.
 *
 * @param discountCode - Code entered by the user (query runs only when non-empty)
 * @returns The result of the validation
 */
export function useValidateDiscountCode(discountCode: string): UseValidateDiscountCodeResult {
  const discountCodeProvided = typeof discountCode === 'string' && discountCode !== '';

  const queryClientRef = useRef(initQueryClient(apiPatientPortalContract, { baseUrl: settings.baseUrl }));
  const tsRestClient = queryClientRef.current;

  const queryKey = [QueryKey.ValidateConsultationDiscount, discountCode];

  const { data, isError, isLoading, refetch } = useQuery({
    queryKey,
    enabled: discountCodeProvided,
    refetchOnMount: 'always',
    staleTime: 1000,
    queryFn: async () => {
      const response = await tsRestClient.validateConsultationDiscount.query({
        params: { discountCode },
      });
      return response;
    },
  });

  return useMemo((): UseValidateDiscountCodeResult => {
    if (isError) {
      return {
        isValidated: undefined,
        data: undefined,
        isError: true,
        errorMessage: 'Unable to validate this discount code. Please try again.',
        isLoading,
        refetch,
      };
    }

    if (!data) {
      return {
        isValidated: undefined,
        data: undefined,
        isError: false,
        errorMessage: undefined,
        isLoading,
        refetch,
      };
    }

    if (data.status === 200) {
      return {
        isValidated: data.body?.data?.validated ?? false,
        data,
        isError: false,
        errorMessage: undefined,
        isLoading,
        refetch,
      };
    }

    return {
      isValidated: false,
      data,
      isError: true,
      errorMessage: errorMessageFromResponse(data),
      isLoading,
      refetch,
    };
  }, [data, isError, isLoading, refetch]);
}
